import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { endOfDay, format, parseISO, startOfDay, subDays } from 'date-fns';
import { useT } from '../../i18n';
import styles from './PeriodControl.module.css';

export interface PeriodRange {
  from: Date;
  to: Date;
}

export type PeriodPreset = 'week' | 'month' | 'quarter' | 'custom';

export interface PeriodControlProps {
  value: PeriodRange;
  onChange: (range: PeriodRange) => void;
}

/** Rolling preset ranges — week/month/quarter ending today. */
export function presetRange(preset: Exclude<PeriodPreset, 'custom'>, now = new Date()): PeriodRange {
  const days = preset === 'week' ? 6 : preset === 'month' ? 29 : 89;
  return { from: startOfDay(subDays(now, days)), to: endOfDay(now) };
}

const DAY = 'yyyy-MM-dd';

function sameDay(a: Date, b: Date): boolean {
  return format(a, DAY) === format(b, DAY);
}

/** Detect which preset the range matches (day precision), else 'custom'. */
export function detectPreset(range: PeriodRange, now = new Date()): PeriodPreset {
  for (const preset of ['week', 'month', 'quarter'] as const) {
    const r = presetRange(preset, now);
    if (sameDay(r.from, range.from) && sameDay(r.to, range.to)) return preset;
  }
  return 'custom';
}

/**
 * Report period control: rolling Week/Month/Quarter presets + custom
 * from/to date range. Week (rolling 7 days) is the dashboard default.
 */
export function PeriodControl({ value, onChange }: PeriodControlProps) {
  const { t } = useT();
  const active = useMemo(() => detectPreset(value), [value]);
  const [rangeError, setRangeError] = useState(false);

  const presets: { key: Exclude<PeriodPreset, 'custom'>; label: string }[] = [
    { key: 'week', label: t('pipeline.periodWeek') },
    { key: 'month', label: t('pipeline.periodMonth') },
    { key: 'quarter', label: t('pipeline.periodQuarter') },
  ];

  const applyCustom = (form: HTMLFormElement) => {
    const data = new FormData(form);
    const fromRaw = String(data.get('from') ?? '');
    const toRaw = String(data.get('to') ?? '');
    const from = fromRaw ? startOfDay(parseISO(fromRaw)) : null;
    const to = toRaw ? endOfDay(parseISO(toRaw)) : null;
    if (!from || !to) return;
    if (from > to) {
      setRangeError(true);
      return;
    }
    setRangeError(false);
    onChange({ from, to });
  };

  return (
    <div className={styles.root}>
      <div className={styles.presets} role="group" aria-label={t('pipeline.funnelTitle')}>
        {presets.map((p) => (
          <button
            key={p.key}
            type="button"
            className={clsx(styles.preset, active === p.key && styles.presetActive)}
            aria-pressed={active === p.key}
            onClick={() => onChange(presetRange(p.key))}
          >
            {p.label}
          </button>
        ))}
      </div>
      <form
        className={styles.range}
        onSubmit={(e) => {
          e.preventDefault();
          applyCustom(e.currentTarget);
        }}
      >
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('pipeline.periodFrom')}</span>
          <input
            className={styles.date}
            type="date"
            name="from"
            key={`from-${format(value.from, DAY)}`}
            defaultValue={format(value.from, DAY)}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('pipeline.periodTo')}</span>
          <input
            className={styles.date}
            type="date"
            name="to"
            key={`to-${format(value.to, DAY)}`}
            defaultValue={format(value.to, DAY)}
          />
        </label>
        <button type="submit" className={styles.apply}>
          {t('pipeline.periodApply')}
        </button>
      </form>
      {rangeError && (
        <div className={styles.rangeError} role="alert">
          {t('pipeline.periodRangeError')}
        </div>
      )}
    </div>
  );
}
